<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Page Header integrated into Form Card -->
            <v-card-title class="page-header py-2 px-3">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">Edit Seller Product Variant</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Update seller product variant pricing and inventory details
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Back Button and Form Badge -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip color="warning" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-pencil</v-icon>
                      Edit Details
                    </v-chip>
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to List
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2" v-if="!isLoading">
              <v-form ref="form" v-model="valid" lazy-validation>
                <!-- Product Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-package-variant-closed</v-icon>
                    Product Information
                  </h3>

                  <v-row v-if="!isSeller">
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.position_id"
                        :items="positionItems"
                        :loading="positionLoading"
                        label="Visibility (Position)"
                        placeholder="All users (no restriction)"
                        outlined
                        clearable
                        :error-messages="errors.position_id"
                        @input="clearError('position_id')"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-account-eye</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.seller_id"
                        :items="sellerItems"
                        label="Seller"
                        placeholder="Select a seller"
                        outlined
                        :rules="[(v) => !!v || 'Seller is required']"
                        :error-messages="errors.seller_id"
                        @input="clearError('seller_id')"
                        class="professional-field"
                        hide-details="auto"
                        required
                        disabled
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-account</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.product_variant_id"
                        :items="productVariantItems"
                        label="Product Variant"
                        placeholder="Select a product variant"
                        outlined
                        :rules="[(v) => !!v || 'Product variant is required']"
                        :error-messages="errors.product_variant_id"
                        @input="clearError('product_variant_id')"
                        class="professional-field"
                        hide-details="auto"
                        required
                        disabled
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-package-variant</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>

                  <v-row v-if="selectedVariant && (selectedVariant.country || selectedVariant.manufacturer)">
                    <v-col cols="12" md="6" v-if="selectedVariant.country">
                      <v-text-field
                        :value="selectedVariant.country"
                        label="Country of Origin"
                        outlined
                        readonly
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-flag</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" v-if="selectedVariant.manufacturer">
                      <v-text-field
                        :value="selectedVariant.manufacturer"
                        label="Manufacturer"
                        outlined
                        readonly
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-factory</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Pricing Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-currency-inr</v-icon>
                    Pricing Information
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.maximum_retail_price"
                        label="Maximum Retail Price"
                        placeholder="Enter MRP"
                        outlined
                        type="number"
                        step="0.01"
                        min="0"
                        :error-messages="errors.maximum_retail_price"
                        @input="clearError('maximum_retail_price')"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-tag</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.selling_price"
                        label="Selling Price"
                        placeholder="Enter selling price"
                        outlined
                        type="number"
                        step="0.01"
                        min="0"
                        :error-messages="errors.selling_price"
                        @input="clearError('selling_price')"
                        class="professional-field"
                        hide-details="auto"
                        :readonly="isSeller"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-currency-inr</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row v-if="!isSeller">
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.discount_price"
                        label="Discount Price"
                        placeholder="Enter discount price"
                        outlined
                        type="number"
                        step="0.01"
                        min="0"
                        :error-messages="errors.discount_price"
                        @input="clearError('discount_price')"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-sale</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.commission_percent"
                        label="Commission Percentage"
                        placeholder="Enter commission %"
                        outlined
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        :error-messages="errors.commission_percent"
                        @input="calculateCommission"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-percent</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row v-if="!isSeller">
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.commission_amount"
                        label="Commission Amount"
                        placeholder="Auto-calculated"
                        outlined
                        type="number"
                        step="0.01"
                        min="0"
                        :error-messages="errors.commission_amount"
                        @input="clearError('commission_amount')"
                        class="professional-field"
                        hide-details="auto"
                        readonly
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-calculator</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.stock"
                        label="Stock Quantity"
                        placeholder="Enter stock quantity"
                        outlined
                        type="number"
                        min="0"
                        :error-messages="errors.stock"
                        @input="clearError('stock')"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-package</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row v-if="isSeller">
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.stock"
                        label="Stock Quantity"
                        placeholder="Enter stock quantity"
                        outlined
                        type="number"
                        min="0"
                        :error-messages="errors.stock"
                        @input="clearError('stock')"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-package</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Settings Section -->
                <div class="form-section mb-2" v-if="!isSeller">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-cog</v-icon>
                    Variant Settings
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-card outlined class="status-card">
                        <v-card-text class="pa-2">
                          <div class="d-flex align-center">
                            <v-icon color="grey" class="mr-3">mdi-toggle-switch</v-icon>
                            <div class="flex-grow-1">
                              <div class="font-weight-medium mb-1">
                                Selling Status
                              </div>
                              <div class="text-caption grey--text">
                                Whether seller is actively selling this variant
                              </div>
                            </div>
                            <v-switch
                              v-model="form.status"
                              color="success"
                              inset
                              hide-details
                              class="ml-3"
                              @change="clearError('status')"
                            ></v-switch>
                          </div>
                          <div class="mt-2">
                            <v-chip
                              small
                              :color="form.status ? 'success' : 'warning'"
                              :text-color="form.status ? 'white' : 'black'"
                            >
                              {{ form.status ? "Selling" : "Not Selling" }}
                            </v-chip>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-card outlined class="status-card">
                        <v-card-text class="pa-2">
                          <div class="d-flex align-center">
                            <v-icon color="grey" class="mr-3">mdi-check-circle</v-icon>
                            <div class="flex-grow-1">
                              <div class="font-weight-medium mb-1">
                                Active Status
                              </div>
                              <div class="text-caption grey--text">
                                Set the active status
                              </div>
                            </div>
                            <v-switch
                              v-model="form.is_active"
                              color="success"
                              inset
                              hide-details
                              class="ml-3"
                              @change="clearError('is_active')"
                            ></v-switch>
                          </div>
                          <div class="mt-2">
                            <v-chip
                              small
                              :color="form.is_active ? 'success' : 'error'"
                              :text-color="form.is_active ? 'white' : 'white'"
                            >
                              {{ form.is_active ? "Active" : "Inactive" }}
                            </v-chip>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
              </v-form>
            </v-card-text>

            <!-- Loading State -->
            <v-card-text v-else class="text-center py-8">
              <health-loader message="Loading details..." />
            </v-card-text>

            <!-- Form Actions Footer -->
            <v-divider></v-divider>
            <v-card-actions class="form-actions pa-2">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <v-chip
                      small
                      :color="hasValidationErrors() ? 'error' : 'success'"
                      :text-color="hasValidationErrors() ? 'white' : 'white'"
                      class="mr-3"
                    >
                      <v-icon left x-small>
                        {{
                          hasValidationErrors()
                            ? "mdi-alert-circle"
                            : "mdi-check-circle"
                        }}
                      </v-icon>
                      {{
                        hasValidationErrors()
                          ? "Please fix validation errors"
                          : "Form validation passed"
                      }}
                    </v-chip>
                    <span class="text-caption grey--text">
                      All required fields must be completed
                    </span>
                  </div>
                </v-col>

                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex justify-end">
                    <v-btn
                      color="grey"
                      outlined
                      class="mr-3 action-button"
                      @click="$router.back()"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      color="primary"
                      class="action-button"
                      @click="update"
                      :loading="isSaving"
                      :disabled="isSaving"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      {{ isSeller ? 'Update MRP' : 'Update Details' }}
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "SellerProductVariantEditPage",
  data() {
    return {
      valid: false,
      isLoading: true,
      isSaving: false,
      form: {
        seller_id: null,
        product_variant_id: null,
        seller_product_id: null,
        position_id: null,
        maximum_retail_price: null,
        selling_price: null,
        discount_price: null,
        commission_percent: null,
        commission_amount: null,
        stock: 0,
        status: true,
        is_active: true,
      },
      originalForm: {},
      errors: {},
      positionItems: [],
      positionLoading: false,
      sellerItems: [],
      productVariantItems: [],
      selectedVariant: null,
    };
  },
  watch: {
    'form.position_id'() {
      this.clearError('position_id');
    },
    'form.seller_id'() {
      this.clearError('seller_id');
    },
    'form.product_variant_id'() {
      this.clearError('product_variant_id');
    },
    'form.seller_product_id'() {
      this.clearError('seller_product_id');
    },
    'form.maximum_retail_price'() {
      this.clearError('maximum_retail_price');
    },
    'form.selling_price'() {
      this.clearError('selling_price');
      this.calculateCommission();
    },
    'form.discount_price'() {
      this.clearError('discount_price');
    },
    'form.commission_percent'() {
      this.clearError('commission_percent');
    },
    'form.commission_amount'() {
      this.clearError('commission_amount');
    },
    'form.stock'() {
      this.clearError('stock');
    },
    'form.status'() {
      this.clearError('status');
    },
    'form.is_active'() {
      this.clearError('is_active');
    },
  },
  computed: {
    isSeller() {
      const pos = this.userPosition && this.userPosition.name ? this.userPosition.name.toUpperCase() : '';
      return pos === 'SELLER';
    },
  },
  async mounted() {
    this.loadPositions();
    await this.fetchData();
  },
  methods: {
    async loadPositions() {
      this.positionLoading = true;
      try {
        const res = await axios.get('/positions', { params: { rowsPerPage: 100 } });
        const records = res.data.data || [];
        this.positionItems = records.map(p => ({ value: p.id, text: p.name }));
      } catch (e) {
        if (e.response?.status === 401) window.location.href = '/auth/login';
      } finally {
        this.positionLoading = false;
      }
    },

    async loadDisplayItems(sellerId, variantId) {
      try {
        const [sellerRes, variantRes] = await Promise.all([
          axios.get(`/users/${this.encryptIt(sellerId)}`),
          axios.get(`/product_variants/${this.encryptIt(variantId)}`),
        ]);
        const u = sellerRes.data.data;
        const v = variantRes.data.data;
        this.sellerItems = [{ value: u.id, text: `${u.first_name} ${u.last_name} (${u.email})` }];
        this.productVariantItems = [{ value: v.id, text: v.sku_id ? `${v.name} (${v.sku_id})` : v.name }];
        this.selectedVariant = v;
      } catch (e) {
        // Non-critical: display fields are disabled anyway
      }
    },

    async fetchData() {
      try {
        const id = this.$route.params.id;
        const response = await axios.get(`/seller_product_variants/${id}`);
        
        if (response.data.success) {
          const data = response.data.data;
          this.form = {
            seller_id: data.seller_id,
            product_variant_id: data.product_variant_id,
            seller_product_id: data.seller_product_id,
            position_id: data.position_id || null,
            maximum_retail_price: data.maximum_retail_price,
            selling_price: data.selling_price,
            discount_price: data.discount_price,
            commission_percent: data.commission_percent,
            commission_amount: data.commission_amount,
            stock: data.stock || 0,
            status: data.status,
            is_active: data.is_active,
          };
          
          this.originalForm = { ...this.form };
          // Load only the two display items — no bulk masters fetch needed
          await this.loadDisplayItems(data.seller_id, data.product_variant_id);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        this.$toast.error('Failed to load details');
        this.$router.back();
      } finally {
        this.isLoading = false;
      }
    },

    clearError(field) {
      if (this.errors[field]) {
        this.$delete(this.errors, field);
      }
    },

    calculateCommission() {
      const sellingPrice = parseFloat(this.form.selling_price) || 0;
      const commissionPercent = parseFloat(this.form.commission_percent) || 0;
      
      if (sellingPrice && commissionPercent) {
        this.form.commission_amount = (sellingPrice * commissionPercent / 100).toFixed(2);
      } else {
        this.form.commission_amount = null;
      }
    },

    hasValidationErrors() {
      return Object.keys(this.errors).length > 0 || !this.valid;
    },

    async update() {
      if (!this.$refs.form.validate()) {
        return;
      }

      try {
        this.isSaving = true;
        this.errors = {};

        let payload;
        if (this.isSeller) {
          payload = {
            maximum_retail_price: this.form.maximum_retail_price ? parseFloat(this.form.maximum_retail_price) : null,
            stock: this.form.stock !== null && this.form.stock !== '' ? parseInt(this.form.stock) : null,
          };
        } else {
          payload = { ...this.form };
          if (payload.maximum_retail_price) payload.maximum_retail_price = parseFloat(payload.maximum_retail_price);
          if (payload.selling_price) payload.selling_price = parseFloat(payload.selling_price);
          if (payload.discount_price) payload.discount_price = parseFloat(payload.discount_price);
          if (payload.commission_percent) payload.commission_percent = parseFloat(payload.commission_percent);
          if (payload.commission_amount) payload.commission_amount = parseFloat(payload.commission_amount);
          if (payload.stock !== null && payload.stock !== '') payload.stock = parseInt(payload.stock);
        }

        const id = this.$route.params.id;
        await axios.put(`/seller_product_variants/${id}`, payload);

        this.$toast.success('Seller product variant updated successfully');
        this.$router.push(this.isSeller ? "/seller-products" : "/seller-product-variants");
      } catch (error) {
        if (error.response && error.response.data.errors) {
          this.errors = error.response.data.errors;
        } else {
          console.error("Error updating seller product variant:", error);
          this.$toast.error('Failed to update seller product variant');
        }
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
/* Professional Form Card with Integrated Header */
.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  border-left: 4px solid var(--v-primary-base);
}

.page-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.back-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

.form-content {
  background: #ffffff;
}

/* Section Styling */
.form-section {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 0.5rem;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

/* Professional Form Fields */
.professional-field >>> .v-input__control {
  min-height: 40px;
}

.professional-field >>> .v-text-field__details {
  padding-top: 4px;
}

.professional-field >>> .v-input__slot {
  border-radius: 4px;
}

.professional-field >>> .v-text-field input {
  font-size: 0.75rem;
  padding-top: 8px;
  padding-bottom: 8px;
}

.professional-field >>> .v-label {
  font-size: 0.75rem;
  font-weight: 500;
}

.professional-field >>> .v-icon {
  color: #6c757d !important;
}

/* Status Card */
.status-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.status-card:hover {
  border-color: var(--v-primary-base);
  background-color: #f8f9fa;
}

/* Form Actions */
.form-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid #dee2e6;
}

.action-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  border-radius: 4px;
  font-size: 0.75rem;
}

/* Spacing and Alignment */
.v-row {
  margin-left: -6px;
  margin-right: -6px;
}

.v-row > .v-col {
  padding: 0 6px;
}

/* Professional Color Scheme */
.v-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
}

.v-btn.v-btn--outlined {
  border: 2px solid currentColor;
}

.v-chip {
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 960px) {
  .page-title {
    font-size: 1.1rem;
  }

  .section-title {
    font-size: 0.85rem;
  }

  .form-content {
    padding: 0.75rem !important;
  }

  .back-button,
  .action-button {
    min-width: 80px;
    font-size: 0.7rem;
  }
}

@media (max-width: 600px) {
  .v-row > .v-col {
    padding: 0 4px;
  }

  .form-content {
    padding: 0.5rem !important;
  }

  .section-title {
    font-size: 0.8rem;
  }

  .professional-field >>> .v-input__control {
    min-height: 36px;
  }

  .professional-field >>> .v-text-field input {
    font-size: 0.7rem;
    padding-top: 6px;
    padding-bottom: 6px;
  }

  .back-button,
  .action-button {
    min-width: 70px;
    height: 28px;
    font-size: 0.65rem;
  }
}

/* Clean Focus States */
.professional-field >>> .v-input--is-focused .v-input__slot {
  border-color: var(--v-primary-base) !important;
  border-width: 2px !important;
}

/* Consistent Margins */
.mb-1 {
  margin-bottom: 0.5rem !important;
}

.mb-2 {
  margin-bottom: 0.75rem !important;
}

.mb-3 {
  margin-bottom: 1rem !important;
}

/* Professional Transitions */
.v-card,
.v-btn,
.professional-field,
.status-card {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>